/* eslint-disable class-methods-use-this */
define(
  'marqueeFilmstripHandler',[
    'builderShowcaseObserver',
    'fastdom',
    'builderShowcaseHelper'
  ],
  (
    builderShowcaseObserver,
    fastdom,
    showcaseHelper,
  ) => {
    'use strict';

    class FilmStrip {
      constructor(configObject) {
        this.config = configObject;
        this.setEvents();
        this.saveFirstImageIntoState();
        this.handleInitialScroll();

        window.addEventListener('orientationchange', () => {
          const activeSelector = '[data-filmstrip-slide] [data-active="true"]';
          const activeImageToUse = this.config.mainViewer.parentElement.querySelector(activeSelector);
          setTimeout(() => {
            this.handleFilmstripScroll(activeImageToUse);
          }, 500);
        });
      }

      /**
     * Sets in the buildershowcase state the active image/item.
       * We could have several filmstrips, so the way of letting know the state which active image belongs
       * to a filmstrip is by saving the active image as an object attribute whom index is the component the
       * active image will belong to. For example, if we want to save an active image for the marquee, then
       * we are going to do «activeImage["marqueeComponent"]=image» and the string "marqueeComponent" it's being read
       * from the config object: «this.config.componentName». Than way, we could have several active images, and
       * we can support N filmstrips at the same page.
     * @param {object} image MarqueeGalleryItem
     */
      setActiveItem(image) {
        let activeImage = this.getStateActiveImage();

        if (!activeImage) {
          activeImage = {};
        }

        activeImage[this.config.componentName] = image;

        builderShowcaseObserver.setData({ activeImage });
        builderShowcaseObserver.commit();
      }

      /**
     * @private
     * Used to save the first image into BSP state
     */
      saveFirstImageIntoState() {
        const firstImage = document.querySelector(this.config.activeThumbSelector);
        if (!firstImage) {
          return;
        }

        const collection = firstImage.dataset.imageCategory;
        if (firstImage) {
          const id = firstImage.dataset.imageId;
          const imageObject = showcaseHelper.getCollectionItem(id, collection, this.config.getGalleryCollection);

          this.setActiveItem(imageObject);
        }
      }

      /**
       * Notifies imageSelected subscribers that a new image has been selected.
       * @param {Object} activeImage MarqueeGalleryItem object
       */
      notifyChange(activeImage) {
        this.config.eventOnImageSelected(activeImage);
      }

      /**
       * Event triggered when a new image in the filmstrip has been selected.
       * Identifies the image selected object.
       * Apply the styles/attributes changes.
       * Updated and notifies the buildershowcase state.
       * @param {*} event event triggered
       */
      setMainImage(selectedThumbnail) {
        const collection = selectedThumbnail.dataset.imageCategory;
        const imageId = selectedThumbnail.dataset.imageId;
        const activeImage = showcaseHelper.getCollectionItem(imageId, collection, this.config.getGalleryCollection);
        showcaseHelper.applyActiveStyles(selectedThumbnail, this.config.activeThumbSelector);
        this.setActiveItem(activeImage);
        this.notifyChange(activeImage);
      }

      /**
     * Function executed when the image selected event is triggered.
     * @param {*} event The event.
     */
      onImageSelected(event) {
        const selectedThumbnail = event.target.closest('LI').querySelector('PICTURE');
        this.setMainImage(selectedThumbnail);
      }

      /**
         * Attaches clicks events to every picture in the filmstrip.
         */
      setEvents() {
        if (this.config.filmstripItems) {
          this.config.filmstripItems.forEach((item) => {
            item.addEventListener('click', this.onImageSelected.bind(this));
          });
        }
      }

      /**
      * Used to get the current active image.
      * @returns The active image.
      */
      getStateActiveImage() {
        const state = builderShowcaseObserver.getState();
        const activeImage = state.data.activeImage;
        return activeImage;
      }

      /**
       * Used to get the number of items inside the current collection.
       * @param {string} collectionCategory The collection name.
       * @returns The number of items inside the current collection.
       */
      getCollectionCount(collectionCategory) {
        const state = builderShowcaseObserver.getState();
        const galleryCollection = this.config.getGalleryCollection(state, collectionCategory);
        return galleryCollection.Items.length;
      }

      /**
       * @private
       * attachControlEvents: attaches the next / prev arrows functionality.
       */
      handleFilmstripScroll(activeImage) {
        const activeImageToUse = activeImage || this.config.firstActiveImage;

        if (!activeImageToUse) {
          return;
        }

        const scroller = activeImageToUse.closest('ul');
        const closestListItem = activeImageToUse.closest('li');
        const currentImageIndex = Array.prototype.indexOf.call(scroller.children, closestListItem);
        const currentImage = scroller.children[currentImageIndex];
        currentImage.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }

      /**
      * @private
      * handleInitialScroll: decides if we need to scroll the filmstrip on init
      */
      handleInitialScroll() {
        const activeImageToUse = this.config.firstActiveImage;

        if (!activeImageToUse) {
          return;
        }

        const imageSequence = parseInt(activeImageToUse.dataset.imageSequence, 10);

        if (imageSequence > 4) {
          this.handleFilmstripScroll(activeImageToUse);
        }
      }

      /**
       * Used to get the next item's index
       * @param {string} data wheter to move to the next or previous image
       * @param {int} activeSequence the current's image sequence
       * @param {*} activeCollectionCount the number of items in the current collection
       * @returns the next item's index
       */
      getNextSequence(data, activeSequence, activeCollectionCount) {
        let sequence = activeSequence;

        switch (data) {
          case 'next':
            sequence += 1;
            break;
          case 'previous':
            sequence -= 1;
            break;
          default:
            sequence = activeSequence;
        }

        if (sequence < 0) {
          return activeCollectionCount - 1;
        }

        if (sequence >= activeCollectionCount) {
          return 0;
        }

        return sequence;
      }

      /**
       * Used to update the page when the event is triggered
       * @param {string} data wheter to move to the next, or previous image
       * @param {string} topic to work once fired the event
       */
      update(data, topic) {
        if (topic === 'onGalleryClosed') {
          const activeItem = this.getStateActiveImage()[this.config.componentName];
          activeItem.playOnLoaded = false;
          builderShowcaseObserver.imageSelected(activeItem);
          return;
        }

        const activeItem = this.getStateActiveImage();
        const pictures = this.config.mainViewer.parentElement.querySelectorAll('[data-filmstrip-slide] picture');
        const filmsStripElements = Array.from(pictures);

        const activeItemId = activeItem[this.config.componentName].Id;
        const activeElement = filmsStripElements.filter((x) => +x.dataset.imageId === activeItemId)[0];
        const activeElementIndex = filmsStripElements.indexOf(activeElement);
        const nextIndex = this.getNextSequence(data, activeElementIndex, filmsStripElements.length);
        const selectedItem = filmsStripElements[nextIndex];

        this.setMainImage(selectedItem);
        this.handleFilmstripScroll(selectedItem);
      }
    }

    return FilmStrip;
  }
);

